import { useEffect, useMemo, useState } from "react";
import ApiService from "../../services/apiService";
import { usePagination } from "../../hooks/usePagination";
import { format } from "date-fns";

import LeftIcon from "../svg/LeftButton";
import RightIcon from "../svg/RightIcon";
import DateTimePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import DownloadExcel from "../downloadExcel/DownloadExcel";

import { Label } from "reactstrap";

import { toast } from "react-toastify";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import LoadingComponent from "../lodingComponent";

interface Props {
  instrumentId: number;
  deviceType: string;
  deviceName : string;
}

const InstrumentHistoryTable = ({
  instrumentId,
  deviceType,
  deviceName
}: Props) => {
  const [refreshKey, setRefreshKey] = useState(0);

  const [fromDateTime, setFromDateTime] = useState<Date | null>(null);
  const [toDateTime, setToDateTime] = useState<Date | null>(null);
  const [isEnabled, setIsEnabled] = useState(false);

  const handleClick = () => {
    resetPagination();
  
    setFromDate(fromDateTime);
    setToDate(toDateTime);
    setId(instrumentId);
  
    setIsEnabled(true);
  
    setRefreshKey((prev) => prev + 1);
  };
  useEffect(() => {
    const now = new Date();

    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);

    setFromDateTime(startOfDay);
    setToDateTime(now);
  }, []);

  const {
    data,
    pageNumber,
    totalPages,
    canGoToNextPage,
    canGoToPreviousPage,
    goToNextPage,
    goToPreviousPage,
    resetPagination,
    setFromDate,
    setToDate,
    setId,
    loading
  } = usePagination({
    endpoint: `/InstrumentHistory/getbyiddatetime`,
    refreshKey,
    enabled: isEnabled,
  });

  useEffect(() => {
     if (!instrumentId) return; if (!instrumentId || !fromDateTime || !toDateTime) return;
   
     resetPagination();
   
     setFromDate(fromDateTime);
     setToDate(toDateTime);
     setId(instrumentId);
   
     setIsEnabled(true); // ADD THIS
   
     setRefreshKey((prev) => prev + 1);
   }, [instrumentId, fromDateTime, toDateTime]);

  const updatedData = useMemo(() => {
     if (!data || !Array.isArray(data)) return [];
   
     return data.map((time: any) => ({
       ...time,
       dateTime: time.dateTime
         ? format(new Date(time.dateTime), "dd-MM-yyyy HH:mm:ss")
         : "-",
     }));
   }, [data]);

  const getColumns = () => {
     const commonColumns = [
       { header: "Date Time", accessor: "dateTime" },
       { header: "Packet", accessor: "packetType" },
       { header: "Join", accessor: "on_Join" },
       { header: "Live Data", accessor: "live_History" },
       { header: "Fixed Interval", accessor: "fixedInterval" },
       { header: "On Request", accessor: "onRequest" },
       { header: "Alarm", accessor: "alarmBit" },
       { header: "Midnight", accessor: "midnightReset" },
       { header: "High Temperature", accessor: "highTemperatureAlarm" },
       { header: "Low Battery", accessor: "lowBatteryAlarm" },
       { header: "Door", accessor: "doorStatus" },
       { header: "Temperature", accessor: "temperature" },
       { header: "Firmware Version", accessor: "firmwareVersion" },
       { header: "Rssi", accessor: "rssi" },
       { header: "Snr", accessor: "snr" },
       { header: "Spreading Factor", accessor: "spreadingFactor" },
     ];
 
     if (deviceType === "Pump Controller" || deviceType === "Pump Comtroller") {
       return [
         ...commonColumns,
         { header: "Battery Voltage", accessor: "batteryVoltage" },
         { header: "Status", accessor: "status" },
         { header: "Operate", accessor: "operateBit" },
         { header: "Acknowledge", accessor: "ackBit" },
         { header: "Watermeter", accessor: "dI1Counter" },
         { header: "Sys. Health", accessor: "p_SysHealthy" },
         { header: "Current Fault", accessor: "p_CurrentFault" },
         { header: "Phase Fault", accessor: "p_PhaseFault" },
         { header: "Phase 1", accessor: "p_Phase1" },
         { header: "Phase 2", accessor: "p_Phase2" },
         { header: "Phase 3", accessor: "p_Phase3" },
         { header: "Power Status", accessor: "p_PowerStatus" },
         { header: "Over Current", accessor: "p_OverCurrentBit" },
         { header: "Under Current", accessor: "p_UnderCurrentBit" },
         { header: "Current Ampere", accessor: "ctValue" },
         { header: "Local Remote", accessor: "p_LocalRemote" },
         {header:"Auto Switch", accessor: "autoSwitch"}
       ];
     }
 
     if (deviceType === "Valve Controller") {
       return [
         ...commonColumns,
         { header: "Status", accessor: "status" },
         { header: "Operate", accessor: "operateBit" },
         { header: "Beacon Event", accessor: "beacon_Event" },
         { header: "Beacon Status", accessor: "beaconStatus" },
         { header: "Battery Voltage", accessor: "batteryVoltage" },
         { header: "Battery Percent", accessor: "battery_Percent" },
         { header: "Readswitch", accessor: "readSwitchHigh" },
         { header: "Watermeter1", accessor: "dI1Counter" },
         { header: "Watermeter2", accessor: "dI2Counter" },
         {header:"Solar Voltage", accessor: "solarVoltage"}
       ];
     }
 
     if (deviceType === "Sensor") {
       return [
         ...commonColumns,
         { header: "Class", accessor: "classType" },
         { header: "Class Change", accessor: "s_ClassChange" },
         { header: "Moisture Bit", accessor: "moistureBit" },
         { header: "Moisture Percentage", accessor: "moisturePercentage" },
         { header: "Battery Voltage", accessor: "batteryVoltage" },
         { header: "Battery Percent", accessor: "battery_Percent" },
         { header: "Dry", accessor: "dry" },
         { header: "Wet", accessor: "wet" },
         { header: "Dry Setpoint", accessor: "dry_SetPoint" },
         { header: "Wet Setpoint", accessor: "wet_SetPoint" },
         { header: "Readswitch", accessor: "readSwitchHigh" },
         { header: "Beacon Status", accessor: "beaconStatus" },
         {header:"Solar Voltage", accessor: "solarVoltage"}
       ];
     }
 
     return commonColumns;
   };


   const fetchAllDataForExport = async () => {
     try {
       const response = await ApiService.axiosApi(
         `/InstrumentHistory/getbyiddatetime`,
         {
           method: "GET",
           params: {
             pageNumber: 0,
             pageSize: 0,
             instrumentId,
             fromDateTime: fromDateTime
               ? format(fromDateTime, "yyyy-MM-dd HH:mm:ss")
               : "",
             toDateTime: toDateTime
               ? format(toDateTime, "yyyy-MM-dd HH:mm:ss")
               : "",
           },
         }
       );
   
       if (response.success) {
         return response.data.data;
       }
   
       toast.error("Unable to fetch full report");
       return [];
     } catch (error) {
       console.log(error);
       toast.error("API Error");
       return [];
     }
   };

   const getFilteredDataForExcel = async () => {
     const columns = getColumns();
   
     const allData = await fetchAllDataForExport();
   
     return allData.map((row: any) => {
       const filteredRow: Record<string, any> = {};
   
       columns.forEach(({ header, accessor }) => {
         const key = accessor?.trim();
   
         let value = row[key];
   
         if (key === "dateTime" && value) {
           value = format(
             new Date(value),
             "dd-MM-yyyy HH:mm:ss"
           );
         }
   
         filteredRow[header] = value ?? "-";
       });
   
       return filteredRow;
     });
   };
  return (
    <>
    
    <div className="table-com-wrap">
    <div className="datetime-bar-wrapper">
  <div className="datetime-bar justify-content-center">

    <div className="datetime-group">
      <span className="dt-label">From</span>
      <div className="dt-input-wrap">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#4B5563"
                    strokeWidth="2"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
      <DateTimePicker
        className="report-input"
        dateFormat="dd/MM/yyyy HH:mm:ss"
        showTimeSelect
        selected={fromDateTime}
        onChange={(date: Date | null) =>
          setFromDateTime(date)
        }
        timeFormat="HH:mm:ss"
      />
      </div>
    </div>

    <div className="dt-arrow">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9CA3AF"
                  strokeWidth="2"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </div>

    <div className="datetime-group">
      <span className="dt-label">To</span>
      <div className="dt-input-wrap">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#4B5563"
                    strokeWidth="2"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>

      <DateTimePicker
        className="report-input"
        dateFormat="dd/MM/yyyy HH:mm:ss"
        showTimeSelect
        selected={toDateTime}
        onChange={(date: Date | null) =>
          setToDateTime(date)
        }
        timeFormat="HH:mm:ss"
      />
      </div>
    </div>

    <div className="dt-divider" />
    <div className="datetime-group">
      <Label style={{ visibility: "hidden" }}>
       
      </Label>

      <button className="filter-btn dt-btn" onClick={handleClick}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                  >
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                  </svg>
                  Generate
                </button>
    </div>


    <div className="d-flex flex-column">
      <Label style={{ visibility: "hidden" }}>
        Export
      </Label>

      <DownloadExcel
        InstrumentName={deviceName}
        getData={getFilteredDataForExcel}
        ExcelSheetName={`${deviceName}_History`}
      />
    </div>
  </div>
</div>
      {loading?<LoadingComponent/>:<div style={{ width: "100%", marginTop: "16px" }}>
        <Table
          wrapperClassName="table-responsive rounded border"
          className="table table-hover table-bordered align-middle text-center mb-0"
        >
          <TableHeader className="bg-light-primary ">
            <TableRow>
              {getColumns().map((column, index) => (
                <TableHead
                  key={index}
                  className="text-center text-nowrap history-table-header"
                >
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {updatedData.map((row: any, rowIndex: number) => (
              <TableRow key={rowIndex} className="">
                {getColumns().map((column, colIndex) => (
                  <TableCell  key={colIndex} className="history-table-row">
                    {row[column.accessor] ?? "-"}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div> }

      <div
        className="d-flex justify-content-end align-content-center"
        style={{ marginRight: "10px", marginTop: "10px" }}
      >
        <p style={{ marginRight: "10px", marginTop: "8px" }}>
          Page {pageNumber} of {totalPages}
        </p>

        <button
          onClick={goToPreviousPage}
          disabled={!canGoToPreviousPage}
          className="icon-button"
        >
          <LeftIcon className="icon" />
        </button>

        <button
          className={`icon-button ${
            !canGoToNextPage ? "disabled" : ""
          }`}
          onClick={goToNextPage}
          disabled={!canGoToNextPage}
        >
          <RightIcon className="icon" />
        </button>
      </div>
      </div>
    </>
  );
};

export default InstrumentHistoryTable;